import React, { useEffect, useRef, useState } from "react";
import { CFormLabel, CFormInput } from "@coreui/react";
import { useTranslationContext } from "@/context/TranslationContext";
import { dummyUser } from "@/constants/translation/dummyUser.translation";

let googleMapsScriptPromise = null;

/**
 * Loads the Google Maps JS API (Places library) once and caches the in-flight
 * promise so multiple mounts of this component don't inject the script twice.
 * Resolves to `null` (instead of rejecting) when no API key is configured, so
 * callers can fall back to plain manual entry of the fields below it.
 */
const loadGoogleMapsScript = (apiKey) => {
  if (!apiKey) return Promise.resolve(null);
  if (window.google?.maps?.places) return Promise.resolve(window.google);

  if (!googleMapsScriptPromise) {
    googleMapsScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.google);
      script.onerror = () => reject(new Error("Failed to load Google Maps script"));
      document.head.appendChild(script);
    });
  }

  return googleMapsScriptPromise;
};

// City isn't a single, consistent Google component type across countries -
// fall back through the closest matches in order of specificity.
const CITY_COMPONENT_TYPES = ["locality", "postal_town", "administrative_area_level_2", "administrative_area_level_1"];

const parseAddressComponents = (components = []) => {
  const findByType = (type) => components.find((component) => component.types.includes(type))?.long_name || "";
  const city = CITY_COMPONENT_TYPES.reduce((found, type) => found || findByType(type), "");

  return {
    house_number: findByType("street_number"),
    street: findByType("route"),
    city,
    country: findByType("country"),
    zip_code: findByType("postal_code"),
  };
};

/**
 * GoogleAddressInput
 *
 * A search-only address lookup: typing shows Google Places autocomplete
 * suggestions, and selecting one parses the place into house number / street /
 * city / country / zip code plus latitude/longitude, handing all of it to
 * `onPlaceSelected` in one shot. The raw search text itself isn't form data -
 * only the parsed fields and coordinates are. Falls back to a plain, inert text
 * input with a configuration note when REACT_APP_GOOGLE_MAPS_API_KEY isn't set,
 * so the address fields below can still be filled in by hand.
 */
const GoogleAddressInput = ({ onPlaceSelected, error }) => {
  const t = useTranslationContext();
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isAutocompleteReady, setIsAutocompleteReady] = useState(false);
  const [hasAuthFailure, setHasAuthFailure] = useState(false);
  const [searchText, setSearchText] = useState("");

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    // Google calls this global when the key is invalid, over quota, or blocked by
    // its own referrer/API restrictions - the most common silent-failure causes.
    window.gm_authFailure = () => {
      console.error(
        "Google Maps API key rejected the request (invalid key, or referrer/API restrictions in Google Cloud Console).",
      );
      setHasAuthFailure(true);
    };

    return () => {
      delete window.gm_authFailure;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    loadGoogleMapsScript(apiKey)
      .then((google) => {
        if (!isMounted || !google || !inputRef.current) return;

        autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
          fields: ["formatted_address", "address_components", "geometry"],
        });

        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current.getPlace();
          if (!place?.geometry?.location) return;

          setSearchText(place.formatted_address || inputRef.current.value);

          onPlaceSelected({
            ...parseAddressComponents(place.address_components),
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
          });
        });

        setIsAutocompleteReady(true);
      })
      .catch((loadError) => {
        console.error("Failed to load Google Maps address autocomplete:", loadError);
        setIsAutocompleteReady(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  return (
    <div className="google-address-input mb-3">
      <CFormLabel>{t(dummyUser.form.address_search_label)}</CFormLabel>
      <CFormInput
        ref={inputRef}
        type="text"
        placeholder={t(dummyUser.form.address_search_placeholder)}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        className={error ? "is-invalid" : ""}
      />
      {error && <div className="error text-danger mt-1">{error}</div>}
      {!apiKey && (
        <div className="text-muted mt-1" style={{ fontSize: "0.8rem" }}>
          {t(dummyUser.form.maps_not_configured_note)}
        </div>
      )}
      {apiKey && hasAuthFailure && (
        <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
          {t(dummyUser.form.maps_key_rejected_note)}
        </div>
      )}
      {apiKey && !hasAuthFailure && !isAutocompleteReady && (
        <div className="text-muted mt-1" style={{ fontSize: "0.8rem" }}>
          {t(dummyUser.form.maps_loading_note)}
        </div>
      )}
    </div>
  );
};

export default GoogleAddressInput;
